






import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import * as def from '@/definitions'
import { Layer } from '@/model/document2/page/surface/design/layer/layer';
import { Vector2 } from '@/utilityModels/vector2';
import { TextLayer } from '@/model/document2/page/surface/design/layer/textLayer';
import { TableLayer } from '@/model/document2/page/surface/design/layer/tableLayer';
import { MathHelper } from '@/helper/mathHelper';

import { UserAgentHelper } from "@/helper/userAgentHelper"
import { NativeHelper } from '@/helper/nativeHelper';
import { DebugHelper } from '@/helper/debugHelper';

import { TextAreaUndoRedoManager } from "./textAreaUndoRedoManager";

@Component({
    components: {
    },
})
export default class InlineTextEditor extends Vue 
{
    // 縦書きかどうか
    @Prop({default : false}) private isVertical! : boolean;

    // テキストのバインディング
    private editText : string = "";

    // エディタでフォーカスをロックする。trueの間は他の入力にフォーカスが渡らない
    private isFocusLock : boolean = true;

    // 直前の選択状態を保持して、復帰用とする
    private lastStart = 0;
    private lastEnd = 0;

    // エディタ内で使用するアンドゥリドゥマネージャ
    undoRedoManager : TextAreaUndoRedoManager = new TextAreaUndoRedoManager();

    get isIE() { return UserAgentHelper.getBrowser() == "ie"; }
    isUndoShortcut(event : KeyboardEvent)
    {
        if(UserAgentHelper.getOS() == "MacOS") return event.metaKey && event.key == 'z';
        return event.ctrlKey && event.key == 'z';
    }

    isRedoShortcut(event : KeyboardEvent)
    {
        if(UserAgentHelper.getOS() == "MacOS") return event.metaKey && event.shiftKey && event.key == 'z';
        return event.ctrlKey && event.shiftKey && event.key == 'z' || event.ctrlKey && event.key == 'y';
    }

    mounted() 
    {
        // メッセージセンター取得
        var messageCenter = this.$store.getters.messageCenter;
        // イベントによりフォーカスロックを外す
        messageCenter.subscribe(this, "unlock_text_editor_focus", (param) => { this.isFocusLock = false; });
        messageCenter.subscribe(this, "lock_text_editor_focus", (param) => { 
            this.isFocusLock = true; 
            this.focus(true);
        });

        if(UserAgentHelper.isElectron())
        {
            // DL版の場合はメニューから呼ばれる可能性があり、このときもテキスト変更を取得する必要があるため

            // Cut, Copy, Paste
            document.addEventListener("cut", this.onKeyEvent);
            document.addEventListener("copy", this.onKeyEvent);
            document.addEventListener("paste", this.onKeyEvent);
        }
    }

    destroyed()
    {
        // メッセージセンター取得
        var messageCenter = this.$store.getters.messageCenter;
        // 購読の削除
        messageCenter.unsubscribe(this, "unlock_text_editor_focus");
        messageCenter.unsubscribe(this, "lock_text_editor_focus");

        if(UserAgentHelper.isElectron())
        {
            // DL版の場合はメニューから呼ばれる可能性があり、このときもテキスト変更を取得する必要があるため

            // Cut, Copy, Paste
            document.removeEventListener("cut", this.onKeyEvent);
            document.removeEventListener("copy", this.onKeyEvent);
            document.removeEventListener("paste", this.onKeyEvent);
        }
    }

    private onKeyEvent(event)
    {
        // テキスト変更
        // すぐにだと反映していないので少し遅らせる
        setTimeout(() =>
        {
            this.onTextChanged(event);
        }, 100);
    }

    private get textAreaStyle()
    {
        // IE, Edgeでキャレットが見えてしまうので、文字サイズは小さくする
        //! ただしelectronで文字サイズを小さくしすぎると入力ができないのでelectronのみ大きくする
        return UserAgentHelper.isElectron() ? { "font-size" : "4px" } : { "font-size" : "1px" };
    }

    private changeInputMode(mode)
    {
        this.$store.dispatch("changeInputMode", mode);
    }

    // テキスト変更監視
    private onTextChanged(event)
    {
        if(event.target != null && event.target.tagName.toUpperCase() == "TEXTAREA")
        {
            // イベント対象がTextAreaのときのみ処理
            this.updatePosition();
            var textarea = event.target as HTMLTextAreaElement;
            // イベント送信
            this.$emit("changeText", { text : textarea.value } );
            this.undoRedoManager.addHistory(textarea);
        }
    }

    // テキスト入力監視
    private onTextInput(event)
    {
        if(event.target != null && event.target.tagName.toUpperCase() == "TEXTAREA")
        {
            if(event.inputType == "historyUndo") this.undo();
            else if(event.inputType == "historyRedo") this.redo();
            else this.undoRedoManager.addHistory(event.target);
        }
    }

    public canUndo()
    {
        return this.undoRedoManager.canUndo;
    }

    public undo()
    {
        let textarea = this.$refs.textarea as HTMLTextAreaElement;
        if(textarea != null)
        {
            this.undoRedoManager.undo(textarea);
            this.update();
        }
    }

    public canRedo()
    {
        return this.undoRedoManager.canRedo;
    }

    public redo()
    {
        let textarea = this.$refs.textarea as HTMLTextAreaElement;
        if(textarea != null)
        {
            this.undoRedoManager.redo(textarea);
            this.update();
        }
    }

    public isTextSelected()
    {
        if(this.isIE)
        {
            return this.lastStart != this.lastEnd;
        }
        else
        {
            let textarea = this.$refs.textarea as HTMLTextAreaElement;
            if(textarea != null)
            {
                return textarea.selectionStart != textarea.selectionEnd;
            }
        }
        return false;
    }

    // フォーカスアウト
    private onBlur()
    {
        // 再フォーカス
        if(this.isFocusLock) 
        { 
            this.focus(true); 
            return;
        }

        // カーソル動作を解放
        this.changeInputMode(0);

        // テキスト確定処理
        this.$emit("fixText");

        if(UserAgentHelper.isElectron())
        {
            // テキスト編集終了をネイティブに通知する
            NativeHelper.sendNativeEvent("end_edit_text", {});
        }
    }

    // 文字列反映
    public setText(text : string)
    {
        this.editText = text;
        
        this.$nextTick(() =>
        {
            var textarea = this.$refs.textarea as HTMLTextAreaElement;
            if(textarea != null)
            {
                this.undoRedoManager.init(textarea);
            }
        });
    }

    // 強制フォーカス
    public focus(reselect : boolean = false, start = 0, end = 0)
    {
        if(UserAgentHelper.getBrowser() == "ie" && this.editText == "")
        {
            // IEでフォーカスのとき文字入力が開始できないバグがあるので対応
            this.editText = " ";
            this.$nextTick(() =>
            {
                this.focusCore(reselect, start, end);
                this.editText = "";
            });
        }
        else
        {
            var process = () =>
            {
                this.$nextTick(() => 
                {
                    this.focusCore(reselect, start, end);
                });
                setTimeout(() =>
                {
                    this.focusCore(reselect, start, end);
                }, 50);
            };

            if(UserAgentHelper.getBrowser() == "ie")
            {
                // ieの場合、ライトコントローラからテキストに戻るときに
                // 文字入力がうまく再開できないことがあるので
                // フォーカス処理を少し遅延する
                setTimeout(() =>
                {
                    if(!reselect)
                    {
                        this.focusCore(reselect, 0, 100);   
                    }
                    process();
                }, 50);
            }
            else
            {
                process();
            }
        }

        if(UserAgentHelper.isElectron())
        {
            // テキスト編集開始をネイティブに通知する
            NativeHelper.sendNativeEvent("start_edit_text", {});
        }
    }

    private focusCore(reselect : boolean, start : number, end : number)
    {
        var textarea = this.$refs.textarea as HTMLTextAreaElement;
        if(textarea != null)
        {
            textarea.focus();
            if(reselect)
            {
                // 再選択する
                DebugHelper.trace("再選択: " + this.lastStart + ", " + this.lastEnd);
                this.setSelection(textarea, this.lastStart, this.lastEnd);
            }
            else
            {
                // カーソルセット
                this.setSelection(textarea, start, end);
            }
            // フォーカスが抜けないようにする
            this.isFocusLock = true;
            // 入力中のカーソル動作をtextareaで独占させる
            this.changeInputMode(1);

            this.$nextTick(() =>
            {
                this.updatePosition();
            });
        }
    }

    public select(start, end)
    {
        var textarea = this.$refs.textarea as HTMLTextAreaElement;
        if(textarea != null)
        {
            this.setSelection(textarea, start, end);
            this.$nextTick(() => 
            {
                this.updatePosition();
                this.focus(true);
            });
        }
    }

    // テキストを選択する
    // IEではこれが呼ばれてはじめてフォーカスが当たる
    private setSelection(textarea : HTMLTextAreaElement, start : number, end : number)
    {
        var fixStart = Math.min(start, end);
        var fixEnd = Math.max(start, end);
        textarea.setSelectionRange(fixStart, fixEnd);
        this.lastStart = fixStart;
        this.lastEnd = fixEnd;
    }

    // IEでは無効。これ呼ぶと選択がおかしくなる
    private setSelectionAll(textarea : HTMLTextAreaElement)
    {
        textarea.select();
    }

    // 強制アンフォーカス
    public unfocus()
    {
        // フォーカスロック解除
        this.isFocusLock = false;
        this.$nextTick(() => 
        {
            var textarea = this.$refs.textarea as HTMLTextAreaElement;
            textarea.blur();
        });
    }

    // キーダウンの処理
    private selectionDirectionForward : boolean = false;
    private onKeydown(event : KeyboardEvent)
    {
        var textarea = event.target as HTMLTextAreaElement;
        if(event.keyCode == 37 || event.keyCode == 38 || event.keyCode == 39 || event.keyCode == 40)
        {
            // カーソルキー

            // デフォルトイベントを発行しない
            event.preventDefault();

            // 終端
            var end = textarea.value.length;
            // 今選択中かどうか
            var isSelect = textarea.selectionStart != textarea.selectionEnd;

            // 新しい開始位置
            var newStart = textarea.selectionStart;
            // 新しい終了位置
            var newEnd = textarea.selectionEnd;

            // カーソルが進む方向か?
            var isCursorForward = this.isVertical ? event.keyCode == 40 || event.keyCode == 37 : event.keyCode == 39 || event.keyCode == 40;

            if(!isCursorForward)
            {
                if(event.shiftKey)
                {
                    // シフト押下中
                    if(isSelect)
                    {
                        if(this.selectionDirectionForward)
                        {
                            // 選択範囲の縮小
                            newEnd--;
                            if(newEnd < newStart) { newStart = newEnd; }
                        }
                        else
                        {
                            // 選択範囲の拡大
                            newStart--;
                        }
                    }
                    else
                    {
                        // 新しく選択範囲を作成
                        newStart--;
                        this.selectionDirectionForward = false;
                    }
                }
                else
                {
                    if(isSelect)
                    {
                        // 選択解除して選択範囲の先頭へ
                        newEnd = newStart;
                    }
                    else
                    {
                        // 1つ前へ
                        newStart--;
                        newEnd--;
                    }
                }
            }
            else
            {
                if(event.shiftKey)
                {
                    // シフト押下中
                    if(isSelect)
                    {
                        if(this.selectionDirectionForward)
                        {
                            // 選択範囲の拡大
                            newEnd++;
                        }
                        else
                        {
                            // 選択範囲の縮小
                            newStart++;
                            if(newEnd < newStart) { newEnd = newStart; }
                        }
                    }
                    else
                    {
                        // 新しく選択範囲を作成
                        newEnd++;
                        this.selectionDirectionForward = true;
                    }
                }
                else
                {
                    if(isSelect)
                    {
                        // 選択解除して選択範囲の終端へ
                        newStart = newEnd;
                    }
                    else
                    {
                        // 1つ次へ
                        newStart++;
                        newEnd++;
                    }
                }
            }

            // 選択状態更新
            newStart = MathHelper.clamp(newStart, 0, end);
            newEnd = MathHelper.clamp(newEnd, 0, end);
            this.setSelection(textarea, newStart, newEnd)
        }
        else if(this.isUndoShortcut(event))
        {
            event.preventDefault();
            this.undo();
        }
        else if(this.isRedoShortcut(event))
        {
            event.preventDefault();
            this.redo();
        }

        this.$nextTick(() =>
        {
            this.updatePosition();
        });
    }

    private onKeyUp()
    {
        this.$nextTick(() =>
        {
            this.updatePosition();
        });
    }

    // ポジション移動時のイベント
    private updatePosition()
    {
        var textarea = this.$refs.textarea as HTMLTextAreaElement;
        this.$emit("changeSelection", { start : textarea.selectionStart, end : textarea.selectionEnd });
        this.lastStart = textarea.selectionStart;
        this.lastEnd = textarea.selectionEnd;
    }

    // 外からアップデート
    public update()
    {
        setTimeout(() =>
        {
            this.updatePosition();
            // イベント送信
            var textarea = this.$refs.textarea as HTMLTextAreaElement;
            this.$emit("changeText", { text : textarea.value } );
        }, 100);
    }

    // テキストの貼り付け
    public paste(text : string)
    {
        text = text || "";
        let textarea = this.$refs.textarea as HTMLTextAreaElement;
        let startPos = this.isIE ? this.lastStart : textarea.selectionStart;
        let endPos = this.isIE ? this.lastEnd : textarea.selectionEnd;
        textarea.value = textarea.value.substring(0, startPos) + text + textarea.value.substring(endPos);
        textarea.setSelectionRange(startPos + text.length, startPos + text.length);
        // 履歴
        this.undoRedoManager.addHistory(textarea);
        // Update
        this.update();
    }

    // テキストの削除
    public deleteSelection()
    {
        let textarea = this.$refs.textarea as HTMLTextAreaElement;
        let startPos = this.isIE ? this.lastStart : textarea.selectionStart;
        let endPos = this.isIE ? this.lastEnd : textarea.selectionEnd;
        textarea.value = textarea.value.substring(0, startPos) + textarea.value.substring(endPos);
        textarea.setSelectionRange(startPos, startPos);
        // 履歴
        this.undoRedoManager.addHistory(textarea);
        // Update
        this.update();
    }

    // テキストの全選択
    public selectAll()
    {
        let textarea = this.$refs.textarea as HTMLTextAreaElement;
        textarea.setSelectionRange(0, textarea.value.length);
        // 履歴
        this.undoRedoManager.addHistory(textarea);
        // Update
        this.update();
    }

    // テキストの選択解除
    public deselect()
    {
        let textarea = this.$refs.textarea as HTMLTextAreaElement;
        textarea.setSelectionRange(textarea.selectionEnd, textarea.selectionEnd);
        // 履歴
        this.undoRedoManager.addHistory(textarea);
        // Update
        this.update();
    }
}
